import { useCallback, useEffect, useState } from 'react';
import cn from 'classnames';

import { ApiKeyCounts, Application, PlanEnum } from 'api-bible-shared-lib/utils/types';
import Container from '../components/Container';
import Chart, { getCountDateValues, getLineTotalCount, ChartLineData } from '../components/Chart';
import Card from '../components/Card';
import { AppHeader } from '../components/AppHeader';
import {
  IconDownload,
  IconCalendar,
  IconSunrise,
  IconFreePlan,
  IconPaidPlan,
} from '../components/Icons';
import { Button } from '../components/Button';
import Select from '../components/Select';
import Pill from '../components/Pill';
import PlanUsage from '../components/PlanUsage';
import { LoadingPage } from '../components/Loading';

import { useAccountUser } from '../contexts/User';
import { useApplications } from '../contexts/Applications';
import { usePlans } from '../contexts/Plans';

import * as portalApi from '../utils/api';
import { lightBlue, pink } from '../utils/color';
import { PriceLabel, getPlanPriceLabel } from '../utils/plans';
import { toLocaleDateTimeString } from '../utils/time';

function ApplicationDashboard({ appId }: { appId: string }) {
  const [timespan, setTimespan] = useState<number>(30);
  const { account } = useAccountUser();
  const { applicationsById } = useApplications();
  const app = applicationsById[appId];
  const [lineData, setLineData] = useState<ChartLineData | undefined>();
  const [lineError, setLineError] = useState<any>();

  const [apiKeyCounts, setApiKeyCounts] = useState<ApiKeyCounts>({
    dailyCount: 0,
    monthlyCount: 0,
    countUpdatedAt: '',
  });

  useEffect(() => {
    setLineData(undefined);
    setLineError(undefined);

    if (account) {
      const date = new Date();

      portalApi
        .getAppReport({
          accountId: account.id,
          appId,
          startDate: new Date(date.getTime() - 60 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
          endDate: date.toISOString().slice(0, 10),
          summary: 'month',
        })
        .then(({ analytics }) => {
          setLineData({
            requestCount: {
              data: getCountDateValues(analytics.requestCount),
              color: pink,
              label: 'API Requests',
            },
          });
        })
        .catch((err: any) => {
          setLineError(err);
        });

      portalApi
        .getAccountAppApiKey({
          accountId: account.id,
          appId,
          apiKey: app.apiKey,
        })
        .then((currentApiKeyCounts) => {
          setApiKeyCounts(currentApiKeyCounts);
        });
    }
  }, [app, account, appId]);

  const countUpdatedAtDate = apiKeyCounts.countUpdatedAt
    ? new Date(apiKeyCounts.countUpdatedAt)
    : null;

  let dailyUsageMessage = '';
  if (countUpdatedAtDate) {
    dailyUsageMessage = `Updated at ${toLocaleDateTimeString(apiKeyCounts.countUpdatedAt)}`;
  }

  let monthlyUsageMessage = '';
  if (app.billingStartAt && app.billingEndAt) {
    monthlyUsageMessage = `From ${new Date(app.billingStartAt).toLocaleDateString()} to ${new Date(
      app.billingEndAt,
    ).toLocaleDateString()}`;
  } else if (countUpdatedAtDate) {
    const monthlyStartDate = new Date(countUpdatedAtDate);
    monthlyStartDate.setDate(1);
    const monthlyEndDate = new Date(countUpdatedAtDate);
    monthlyEndDate.setMonth(monthlyStartDate.getMonth() + 1);
    monthlyEndDate.setDate(0);
    monthlyUsageMessage = `From ${monthlyStartDate.toLocaleDateString()} to ${monthlyEndDate.toLocaleDateString()}`;
  }

  // excessive plan usage calculations
  // calculate overage for just premium users
  const { plansById } = usePlans();
  if (!Object.keys(plansById).length) return <LoadingPage />;

  const plan = plansById[app.plan];
  const meteredPrice = plan?.prices?.find((price) => price.usage_type === 'metered');

  let baseOverage = 0;
  let baseOverageChargeLabel = '';
  if (app.monthlyBaseLimit) {
    baseOverage = apiKeyCounts.monthlyCount - app.monthlyBaseLimit;
    baseOverageChargeLabel = `$${(
      (baseOverage * (meteredPrice?.unit_amount ?? 0)) /
      1000 /
      100
    ).toFixed(2)} overage`;
  }

  const monthlyOverage = app.monthlyLimit ? apiKeyCounts.monthlyCount - app.monthlyLimit : 0;

  const dates = lineData
    ? Object.values(lineData.requestCount.data)
        .map((datum) => datum.date)
        .slice(-1 * timespan)
    : [];

  if (!app) return <div className="text-white">App not found</div>;

  return (
    <Container>
      <AppHeader
        appLinkBasePath="/application"
        currentAppId={appId}
        currentAppMenuItem="dashboard"
      />
      <div className="flex flex-col mb-8 lg:flex-row">
        <Card dark className="flex w-full lg:w-2/3 items-center px-8 mb-8 lg:mb-0 lg:mr-4">
          <div className="w-full">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold mb-1.5">API Usage</h3>
              {monthlyOverage > 0 && (
                <Pill className="text-white mb-2" color="red">
                  Exceeded Monthly Usage
                </Pill>
              )}
            </div>
            <div
              className="lg:flex lg:flex-row lg:space-x-4 items-center "
              style={{ color: lightBlue }}
            >
              <div className="flex-col flex-1 h-full">
                <PlanUsage
                  count={apiKeyCounts.dailyCount}
                  limit={app.dailyLimit || app.monthlyBaseLimit || app.monthlyLimit}
                  message={dailyUsageMessage}
                >
                  <IconSunrise className="inline w-5 h-5 mr-2" /> Daily Usage
                </PlanUsage>
              </div>
              <div className="flex-col flex-1 h-full">
                <PlanUsage
                  count={apiKeyCounts.monthlyCount}
                  limit={app.monthlyBaseLimit || app.monthlyLimit}
                  message={monthlyUsageMessage}
                >
                  <IconCalendar className="inline w-5 h-5 mr-2" />
                  Monthly Usage
                </PlanUsage>
              </div>
            </div>
          </div>
        </Card>
        <Card dark className="flex w-full lg:w-1/3 items-center px-8 lg:ml-4">
          <div className="w-full">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold mb-1.5">Your Plan</h3>
              {baseOverage > 0 && (
                <Pill className="text-white mb-2" color="red">
                  {baseOverageChargeLabel}
                </Pill>
              )}
            </div>
            <div className="flex flex-row items-center" style={{ color: lightBlue }}>
              <AppDetails
                app={app}
                planName={plansById[app.plan].name ?? ''}
                priceLabel={getPlanPriceLabel(plan.prices)}
              />
            </div>
          </div>
        </Card>
      </div>

      <div className="flex justify-between w-full mb-4">
        <Select label={`${timespan} Days `}>
          <button
            type="button"
            className="block w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 hover:text-gray-900"
            onClick={() => setTimespan(7)}
          >
            7 Days
          </button>
          <button
            type="button"
            className="block w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 hover:text-gray-900"
            onClick={() => setTimespan(30)}
          >
            30 Days
          </button>
          <button
            type="button"
            className="block w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 hover:text-gray-900"
            onClick={() => setTimespan(60)}
          >
            60 Days
          </button>
        </Select>

        <Button>
          Usage <IconDownload className="w-5 h-5 ml-2" />
        </Button>
      </div>

      <Card dark className="mb-8">
        <div className="px-8 pt-2 pb-3 text-white">
          <ul className="flex flex-wrap">
            {!lineData && !lineError && (
              <li className="w-full">
                <h3 className="text-lg text-center">Loading...</h3>
              </li>
            )}
            {lineError && (
              <li className="w-full">
                <h3 className="text-lg text-center text-red-500">Error loading data</h3>
              </li>
            )}
            {/* Grand Totals */}
            {lineData && !lineError && (
              <li className="mr-12">
                <span style={{ color: pink }} className="text-xl font-bold">
                  API Requests
                </span>
                <h3 className="mt-1 text-5xl font-bold tracking-wide">
                  {getLineTotalCount(lineData.requestCount)}
                </h3>
              </li>
            )}
          </ul>
        </div>
        {lineData && !lineError && (
          <>
            <div className="">
              <Chart width={1218} height={240} lineData={lineData} activeLine="requestCount" />
            </div>
            <MonthMarkers
              className="border-t border-blue-500 text-white -mt-px pt-2.5"
              dates={dates}
            />
          </>
        )}
      </Card>
    </Container>
  );
}

function MonthMarkers({ className, dates }: { className: string; dates: Date[] }) {
  const [containerWidth, setContainerWidth] = useState(0);

  const containerRef = useCallback((container) => {
    if (container !== null) {
      setContainerWidth(container.getBoundingClientRect().width);
    }
  }, []);

  const xInterval = containerWidth / (dates.length - 1);

  const months = dates
    .sort((a, b) => (a < b ? -1 : 1))
    .map((date, index: number) => [date.getDate(), index, date])
    .filter(([dayOfMonth]) => dayOfMonth === 1)
    .map(([, index, date]) => [
      // @ts-ignore
      index * xInterval,
      // @ts-ignore
      `${date.toLocaleString('default', {
        month: 'long',
      } as Intl.NumberFormatOptions)} ${(date as Date).getFullYear()}`,
    ]);

  return (
    <div ref={containerRef} className={cn(className, 'relative')}>
      <div className="flex justify-between">
        <p className="opacity-0 user-select-none">Months:</p>
        {containerWidth > 0 &&
          months.map(([xPosition, label]) => (
            <p className="absolute" style={{ left: xPosition, color: '#B5D2FF' }} key={label}>
              {label}
            </p>
          ))}
      </div>
    </div>
  );
}

const AppDetails = ({
  app,
  planName,
  priceLabel,
}: {
  app: Application;
  planName: string;
  priceLabel: PriceLabel;
}) => {
  let renewalDate;
  if (app.plan === PlanEnum.FREE) {
    renewalDate = new Date();
    renewalDate.setMonth(renewalDate.getMonth() + 1);
    renewalDate.setDate(1);
    renewalDate.setUTCHours(0); // counters are reset midnight UTC
    renewalDate.setUTCMinutes(0);
    renewalDate.setUTCSeconds(0);
  } else if (app.billingEndAt) {
    renewalDate = new Date(app.billingEndAt);
  }
  const planRenewalLabel = renewalDate ? `Renews on ${toLocaleDateTimeString(renewalDate)}` : '';

  return (
    <div className="flex flex-col w-full justify-between">
      <div className="w-full mt-2 text-sm">
        {app.plan === PlanEnum.FREE ? (
          <IconFreePlan className="inline w-5 h-5 mr-2" />
        ) : (
          <IconPaidPlan className="inline w-5 h-5 mr-2" />
        )}
        {planName}
      </div>
      <div>
        <div className="flex items-center justify-between">
          <p className="text-4xl font-bold text-white">
            {priceLabel.amount}
            <span className="px-1 opacity-50">{priceLabel.unit}</span>
          </p>
        </div>
      </div>
      <div className="mt-2" />
      <div className="mt-2 text-sm whitespace-nowrap">{planRenewalLabel} &nbsp;</div>
    </div>
  );
};

export default ApplicationDashboard;
